import React, { useCallback, useEffect, useMemo, useRef } from "react";
import { loadCanvasImage, parsePercent } from "../lib/canvasImage";

interface AvatarCanvasIdleProps {
  imageUrl: string;
  eyesHalfBlinkUrl: string;
  eyesBlinkUrl: string;
  eyeMaskUrl?: string;
  suppressBlink?: boolean;
  eyePosition?: { top: string; left: string; width: string; height: string };
  internalWidth?: number;
  internalHeight?: number;
  alt?: string;
}

type BlinkPhase = "open" | "half_closing" | "closed" | "half_opening";

const BLINK_TIMING = {
  half_closing: 30,
  closed: 90,
  half_opening: 30,
};

interface CoverSourceRect {
  sourceX: number;
  sourceY: number;
  sourceWidth: number;
  sourceHeight: number;
}

interface CanvasRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

function getCoverSourceRect(
  image: HTMLImageElement,
  width: number,
  height: number
): CoverSourceRect {
  const imageAspect = image.naturalWidth / image.naturalHeight;
  const canvasAspect = width / height;
  let sourceX = 0;
  let sourceY = 0;
  let sourceWidth = image.naturalWidth;
  let sourceHeight = image.naturalHeight;

  if (imageAspect > canvasAspect) {
    sourceWidth = image.naturalHeight * canvasAspect;
    sourceX = (image.naturalWidth - sourceWidth) / 2;
  } else {
    sourceHeight = image.naturalWidth / canvasAspect;
    sourceY = (image.naturalHeight - sourceHeight) / 2;
  }

  return { sourceX, sourceY, sourceWidth, sourceHeight };
}

function drawCoverImage(
  context: CanvasRenderingContext2D,
  image: HTMLImageElement,
  width: number,
  height: number
) {
  const source = getCoverSourceRect(image, width, height);

  context.drawImage(
    image,
    source.sourceX,
    source.sourceY,
    source.sourceWidth,
    source.sourceHeight,
    0,
    0,
    width,
    height
  );
}

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

function getOrCreateCanvas(
  ref: React.MutableRefObject<HTMLCanvasElement | null>,
  width: number,
  height: number
): HTMLCanvasElement {
  if (!ref.current) {
    ref.current = document.createElement("canvas");
  }
  if (ref.current.width !== width || ref.current.height !== height) {
    ref.current.width = width;
    ref.current.height = height;
  }
  return ref.current;
}

function getEyePatchRect(
  width: number,
  height: number,
  eyePosition: AvatarCanvasIdleProps["eyePosition"],
  expand: boolean
): CanvasRect {
  const left = parsePercent(eyePosition?.left, 0.398);
  const top = parsePercent(eyePosition?.top, 0.378);
  const eyeWidth = parsePercent(eyePosition?.width, 0.1625);
  const eyeHeight = parsePercent(eyePosition?.height, 0.054);
  const padX = expand ? Math.max(0.025, eyeWidth * 0.28) : 0;
  const padTopY = expand ? Math.max(0.07, eyeHeight * 1.25) : 0;
  const padBottomY = expand ? Math.max(0.018, eyeHeight * 0.35) : 0;
  const extraWidth = expand ? Math.max(0.05, eyeWidth * 0.54) : 0;
  const xPct = clamp(left - padX, 0, 1);
  const yPct = clamp(top - padTopY, 0, 1);
  const widthPct = clamp(eyeWidth + extraWidth, 0.01, 1 - xPct);
  const heightPct = clamp(eyeHeight + padTopY + padBottomY, 0.01, 1 - yPct);

  return {
    x: xPct * width,
    y: yPct * height,
    width: widthPct * width,
    height: heightPct * height,
  };
}

function drawFullFaceBlinkEyePatch(
  context: CanvasRenderingContext2D,
  image: HTMLImageElement,
  width: number,
  height: number,
  eyePosition: AvatarCanvasIdleProps["eyePosition"]
) {
  const rect = getEyePatchRect(width, height, eyePosition, true);
  const source = getCoverSourceRect(image, width, height);
  const sourceX = source.sourceX + (rect.x / width) * source.sourceWidth;
  const sourceY = source.sourceY + (rect.y / height) * source.sourceHeight;
  const sourceWidth = (rect.width / width) * source.sourceWidth;
  const sourceHeight = (rect.height / height) * source.sourceHeight;

  context.drawImage(
    image,
    sourceX,
    sourceY,
    sourceWidth,
    sourceHeight,
    rect.x,
    rect.y,
    rect.width,
    rect.height
  );
}

function drawBlinkOverlay(
  context: CanvasRenderingContext2D,
  layerCanvas: HTMLCanvasElement,
  image: HTMLImageElement,
  width: number,
  height: number,
  eyePosition: AvatarCanvasIdleProps["eyePosition"],
  opacity: number,
  maskImage?: HTMLImageElement
) {
  if (opacity <= 0) return;

  if (image.naturalWidth > 500 && maskImage) {
    const layerContext = layerCanvas.getContext("2d");
    if (!layerContext) return;

    layerContext.clearRect(0, 0, width, height);
    layerContext.save();
    layerContext.imageSmoothingEnabled = true;
    layerContext.imageSmoothingQuality = "high";
    drawCoverImage(layerContext, image, width, height);
    layerContext.globalCompositeOperation = "destination-in";
    drawCoverImage(layerContext, maskImage, width, height);
    layerContext.restore();

    context.save();
    context.globalAlpha = opacity;
    context.drawImage(layerCanvas, 0, 0);
    context.restore();
    return;
  }

  context.save();
  context.globalAlpha = opacity;

  if (image.naturalWidth > 500) {
    drawFullFaceBlinkEyePatch(context, image, width, height, eyePosition);
  } else {
    const rect = getEyePatchRect(width, height, eyePosition, false);
    context.drawImage(image, rect.x, rect.y, rect.width, rect.height);
  }

  context.restore();
}

const AvatarCanvasIdle: React.FC<AvatarCanvasIdleProps> = ({
  imageUrl,
  eyesHalfBlinkUrl,
  eyesBlinkUrl,
  eyeMaskUrl,
  suppressBlink = false,
  eyePosition,
  internalWidth = 960,
  internalHeight = 540,
  alt = "avatar",
}) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const blinkLayerRef = useRef<HTMLCanvasElement | null>(null);
  const imagesRef = useRef<{
    base?: HTMLImageElement;
    halfBlink?: HTMLImageElement;
    closedBlink?: HTMLImageElement;
    eyeMask?: HTMLImageElement;
  }>({});
  const blinkPhaseRef = useRef<BlinkPhase>("open");
  const blinkTimerRef = useRef<number>(0);
  const blinkResumeAtRef = useRef<number>(0);
  const blinkSequenceRef = useRef<number>(0);

  const size = useMemo(() => ({ width: internalWidth, height: internalHeight }), [internalHeight, internalWidth]);

  const renderFrame = useCallback(() => {
    const canvas = canvasRef.current;
    const context = canvas?.getContext("2d");
    const baseImage = imagesRef.current.base;
    if (!canvas || !context || !baseImage) return;

    context.clearRect(0, 0, size.width, size.height);
    context.imageSmoothingEnabled = true;
    context.imageSmoothingQuality = "high";
    drawCoverImage(context, baseImage, size.width, size.height);
    const blinkLayer = getOrCreateCanvas(blinkLayerRef, size.width, size.height);

    const phase = blinkPhaseRef.current;
    const halfOpacity =
      phase === "half_closing" || phase === "half_opening"
        ? 1
        : phase === "closed"
        ? 0.32
        : 0;
    const closedOpacity = phase === "closed" ? 1 : 0;
    const halfBlink = imagesRef.current.halfBlink;
    const closedBlink = imagesRef.current.closedBlink;
    const eyeMask = imagesRef.current.eyeMask;

    if (halfBlink) drawBlinkOverlay(context, blinkLayer, halfBlink, size.width, size.height, eyePosition, halfOpacity, eyeMask);
    if (closedBlink) drawBlinkOverlay(context, blinkLayer, closedBlink, size.width, size.height, eyePosition, closedOpacity, eyeMask);
  }, [eyePosition, size.height, size.width]);

  const applyBlinkPhase = useCallback((phase: BlinkPhase) => {
    blinkPhaseRef.current = phase;
    renderFrame();
  }, [renderFrame]);

  const doSingleBlink = useCallback((): Promise<void> => {
    return new Promise((resolve) => {
      const sequence = ++blinkSequenceRef.current;
      const isActive = () => blinkSequenceRef.current === sequence;
      applyBlinkPhase("half_closing");
      window.setTimeout(() => {
        if (!isActive()) {
          resolve();
          return;
        }
        applyBlinkPhase("closed");
        window.setTimeout(() => {
          if (!isActive()) {
            resolve();
            return;
          }
          applyBlinkPhase("half_opening");
          window.setTimeout(() => {
            if (!isActive()) {
              resolve();
              return;
            }
            applyBlinkPhase("open");
            resolve();
          }, BLINK_TIMING.half_opening);
        }, BLINK_TIMING.closed);
      }, BLINK_TIMING.half_closing);
    });
  }, [applyBlinkPhase]);

  const doBlink = useCallback(() => {
    const isDouble = Math.random() < 0.3;
    doSingleBlink().then(() => {
      if (isDouble && Date.now() >= blinkResumeAtRef.current) {
        window.setTimeout(() => {
          if (Date.now() >= blinkResumeAtRef.current) void doSingleBlink();
        }, 100);
      }
    });
  }, [doSingleBlink]);

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      loadCanvasImage(imageUrl),
      loadCanvasImage(eyesHalfBlinkUrl).catch(() => undefined),
      loadCanvasImage(eyesBlinkUrl).catch(() => undefined),
      eyeMaskUrl ? loadCanvasImage(eyeMaskUrl).catch(() => undefined) : Promise.resolve(undefined),
    ]).then(([base, halfBlink, closedBlink, eyeMask]) => {
      if (cancelled) return;
      imagesRef.current = { base, halfBlink, closedBlink, eyeMask };
      renderFrame();
    }).catch((error) => {
      console.error("[AvatarCanvasIdle] image preload failed", error);
    });

    return () => {
      cancelled = true;
    };
  }, [eyeMaskUrl, eyesBlinkUrl, eyesHalfBlinkUrl, imageUrl, renderFrame]);

  useEffect(() => {
    const now = Date.now();
    if (suppressBlink) {
      blinkSequenceRef.current += 1;
      blinkResumeAtRef.current = now + 360;
      applyBlinkPhase("open");
      return;
    }

    blinkResumeAtRef.current = Math.max(blinkResumeAtRef.current, now + 260);
  }, [applyBlinkPhase, suppressBlink]);

  useEffect(() => {
    if (suppressBlink) return;

    const scheduleBlink = (delayOverride?: number) => {
      const delay = delayOverride ?? (2500 + Math.random() * 4000);
      blinkTimerRef.current = window.setTimeout(() => {
        if (Date.now() < blinkResumeAtRef.current) {
          scheduleBlink(220);
          return;
        }

        doBlink();
        scheduleBlink();
      }, delay);
    };

    scheduleBlink();
    return () => {
      window.clearTimeout(blinkTimerRef.current);
    };
  }, [doBlink, suppressBlink]);

  return (
    <canvas
      ref={canvasRef}
      width={size.width}
      height={size.height}
      aria-label={alt}
      role="img"
      className="h-full w-full"
      style={{ display: "block" }}
    />
  );
};

export default AvatarCanvasIdle;
